import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { ResetPasswordDialogComponent } from './reset-password-dialog.component';

// Add / edit an employee membership (v2 root model). Returns a typed result the
// parent maps to the isolated MembershipV2Service. Email is immutable on edit
// (it's the global employee identity). isTest defaults OFF — real production data.
@Component({
  selector: 'v2-employee-dialog',
  templateUrl: './employee-dialog.component.html',
  styleUrls: ['./dialog-shared.css']
})
export class EmployeeDialogComponent {
  model: any;
  resettingPassword = false;
  sendingResetEmail = false;

  constructor(
    public ref: MatDialogRef<EmployeeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private membershipV2: MembershipV2Service
  ) {
    if (data.mode === 'edit') {
      const e = data.employee || {};
      this.model = {
        name: e.name || '',
        employeeCode: e.employeeCode || '',
        groupId: e.groupId || null,
        status: e.status !== false,
        isAdmin: e.isAdmin === true
      };
    } else {
      this.model = {
        name: '', email: '', employeeCode: '', password: '',
        groupId: null, isAdmin: false, isTest: false, sendInvite: true
      };
    }
  }

  save() {
    if (this.data.mode === 'add') {
      if (!this.model.name && !this.model.email) { return; }
      this.ref.close({
        action: 'add',
        isTest: this.model.isTest === true,
        payload: {
          name: this.model.name,
          email: this.model.email,
          employeeId: this.model.employeeCode,
          password: this.model.password,
          groupId: this.model.groupId,
          isAdmin: this.model.isAdmin === true,
          sendInvite: this.model.sendInvite === true && !!this.model.email
        }
      });
    } else {
      this.ref.close({
        action: 'edit',
        patch: {
          name: this.model.name,
          employeeCode: this.model.employeeCode,
          groupId: this.model.groupId || null,
          status: this.model.status === true,
          isAdmin: this.model.isAdmin === true
        }
      });
    }
  }

  // Sets a new password directly — the admin shares it with the employee
  // themselves (e.g. over the phone). Independent of Save/Cancel: takes
  // effect immediately, does not require closing this dialog.
  resetPassword() {
    const employeeId = this.data.employee?.employeeId;
    if (!employeeId) { return; }
    const ref = this.dialog.open(ResetPasswordDialogComponent, {
      width: '420px',
      data: { name: this.data.employee.name || this.data.employee.email, email: this.data.employee.email }
    });
    ref.afterClosed().subscribe(async res => {
      if (!res || !res.password) { return; }
      this.resettingPassword = true;
      const ok = await this.membershipV2.adminResetPassword(employeeId, res.password);
      this.resettingPassword = false;
      if (ok) {
        alert(`New password for ${this.data.employee.email}:\n\n${res.password}\n\nShare it with them now — it won't be shown again.`);
      }
    });
  }

  // Alternative to resetPassword(): emails the employee a link to set their
  // own password, instead of the admin handing them one directly.
  async sendResetEmail() {
    const email = this.data.employee?.email;
    if (!email) { return; }
    this.sendingResetEmail = true;
    await this.membershipV2.sendResetEmail(email);
    this.sendingResetEmail = false;
  }
}
